import React, { ReactElement } from "react";
import styled from "styled-components";

import { ResourceStrings } from "../../../ResourceStrings";
import Modal from "@fluentui/react/lib/Modal";
import Button from "../../shared/Button";
import * as urls from "../../../constants/urls";
import { App } from "../../../app";
import { forMinWidth } from "../../../theme/breakpoints";

interface Props {
    title?: string;
    text?: string;
    result: (action: string) => void; // action is one of: ok, cancel
    isOpen: boolean; // true if the dialog is open, false otherwise
}

// a popup with dropdown-select for a single option out of several
// used for sort by vote/date/etc and similar tasks
export default class ModalDialogSysLogs extends React.Component<Props, never> {
    public ResourceStrings: ResourceStrings = ResourceStrings;
    refFirstFocusElement: React.RefObject<HTMLButtonElement>;

    constructor(props: Props) {
        super(props);

        this.refFirstFocusElement = React.createRef();
    }

    componentDidMount(): void {
        setTimeout(() => {
            if (this.refFirstFocusElement.current == null) {
                return;
            }

            this.refFirstFocusElement.current.focus();
        }, (10));
    }

    private closeDialog(action: string): void {
        // @TODO: Replace 'shutdownpopup' with new equivalent if needed
        ////this.shutdownPopup();
        this.props.result(action);
    }

    public requestClose(): void {
        this.closeDialog('exclude');
    }

    public requestAccept(): void {
        this.closeDialog('include');
    }

    public includeLogsClick(): void {
        this.closeDialog('include');
    }

    public removeLogsClick(): void {
        this.closeDialog('exclude');
    }

    public getExplanationNarrator(): string {
        const privacyStatement = ResourceStrings.msftPrivacyStatement();
        const dataWeCollect = ResourceStrings.dataWeCollect();
        return ResourceStrings.dialogSysLogsExplanation(privacyStatement, dataWeCollect);
    }

    public getExplanationHtml(): string {
        const privacyStatement = `<a href=${urls.getPrivacyStatementUrl()} target="_blank" style="color: var(--color-on-surface-link)">${ResourceStrings.msftPrivacyStatement()}</a>`;
        const dataWeCollect = `<a href=${urls.dataWeCollect} target="_blank" style="color: var(--color-on-surface-link)">${ResourceStrings.dataWeCollect()}</a>`;
        return ResourceStrings.dialogSysLogsExplanation(privacyStatement, dataWeCollect);
    }

    render(): ReactElement {
        return <Modal
            isOpen={this.props.isOpen}
            isBlocking={true}
            onDismiss={() => this.requestClose()}
        >
            <Container role="dialog" aria-label={ResourceStrings.dialogSysLogsTitle()}>
                <div>
                    <div className="header">{ResourceStrings.dialogSysLogsTitle()}</div>
                    <div className="view">
                        <div
                            className="text"
                            aria-label={this.getExplanationNarrator()}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: App.EscapeHtmlRawDisplay(this.getExplanationHtml()) }} />
                    </div>
                    <div className="footer">
                        <Button
                            ref={this.refFirstFocusElement}
                            onClick={() => this.includeLogsClick()}>
                            {ResourceStrings.includeSystemLogs()}
                        </Button>
                        <Button
                            buttonType="normal"
                            onClick={() => this.removeLogsClick()}>
                            {ResourceStrings.removeSystemLogs()}
                        </Button>
                    </div>
                </div>
            </Container>
        </Modal>
    }
}

const Container = styled.div`
    width: 65rem;
    max-width: 100%;
    border: 0.1rem solid #3399ff;
    box-shadow: 0.2rem 0.2rem #000000 20% 0.1rem;

    .header {
        font-size: 2.8rem;
        margin: 2rem 2rem 0rem 2rem;
        color: #1e1e1e;
    }

    .footer {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 2rem;
        flex-shrink: 0;
        border-top: 0.1rem solid #ccc;

        >* + * {
            margin-top: 2rem;
        }

        ${forMinWidth.sm`
            flex-direction: row;

            >* + * {
                margin-top: 0;
            }
        `}
    }

    .view {
        padding: 2rem;
        overflow-y: auto;
        flex: 1 1 auto;
    }

    .text {
        line-height: 2.4rem;
    }
`;